import { submitAcknowledgment } from '@ctw/shared/api/acknowledgments/submit-acknowledgment';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useResilientStorage } from '@ctw/shared/hooks/use-resilient-storage';
import { addMonths, format, isValid, parseISO } from 'date-fns';
import { useRef } from 'react';

// Acknowledgments get their own namespaced top-level localStorage keys rather than a slot in the
// `zui-preferences` blob: they are workflow state with meaning beyond presentation, and a future
// "reset UI preferences" sweep must not silently wipe them. Mirrors the `zus:atlas:excluded-fields:`
// pattern. The key is only ever overwritten, never pruned.
const STORAGE_KEY_PREFIX = 'zus:acknowledgments:';

interface StoredAcknowledgment {
  /** The disclosure version the user last acknowledged. */
  version: string;
  /** Local calendar date (YYYY-MM-DD) of that acknowledgment. */
  acknowledgedAt: string;
}

export interface UseAcknowledgmentOptions {
  /** The acknowledgment use case, e.g. 'atlas-export-phi'. Namespaces both the durable record and the local gate. */
  kind: string;
  /** Current disclosure version. A change forces re-acknowledgment; recorded for legal proof. */
  version: string;
  /** The exact disclosure text shown, persisted with the durable record for self-contained proof. */
  disclosureText?: string;
  /** How long a single acknowledgment stays valid. Defaults to 6 months. */
  intervalMonths?: number;
}

export interface Acknowledgment {
  /** True when the user has never acknowledged, the version changed, or the last one lapsed. */
  requiresAcknowledgment: boolean;
  /** When the current acknowledgment lapses; undefined if never acknowledged. */
  expiresAt: Date | undefined;
  /**
   * Records the acknowledgment for the current user: writes the durable audit record via the BFF
   * first — throwing if that fails, so the caller can block the gated action — then stamps the
   * local gate. Call only when `requiresAcknowledgment` (i.e. the user actually acknowledged);
   * within-window actions are covered by the prior acknowledgment and should record nothing.
   */
  recordAcknowledgment: (context?: Record<string, string | number | boolean>) => Promise<void>;
}

// Guards corrupt/legacy values (valid JSON of the wrong shape) — anything unrecognized reads as
// "never acknowledged".
const parseStored = (value: unknown): StoredAcknowledgment | undefined => {
  if (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as StoredAcknowledgment).version === 'string' &&
    typeof (value as StoredAcknowledgment).acknowledgedAt === 'string'
  ) {
    return value as StoredAcknowledgment;
  }
  return undefined;
};

// Parses the stored acknowledgment date as local midnight. `parseISO` reads a `YYYY-MM-DD` string
// as local time (unlike `new Date('YYYY-MM-DD')`, which parses as UTC and shifts the day across
// timezones), matching `formatDateToLocalString`.
const parseLocalDate = (iso: string): Date | undefined => {
  const parsed = parseISO(iso);
  return isValid(parsed) ? parsed : undefined;
};

// Lightweight obfuscation of the stored value — deterrence, NOT security. It keeps the gate from
// sitting in localStorage as an obviously-editable plaintext date; a determined user can still
// reverse it (the key is in this bundle), which is exactly why the authoritative, tamper-proof
// record is the server-side audit log. XOR is symmetric, so the same cipher encodes and decodes.
const OBFUSCATION_KEY = 'ctw-acknowledgment';

const xorCipher = (input: string): string =>
  Array.from(input, (char, index) =>
    String.fromCharCode(
      char.charCodeAt(0) ^ OBFUSCATION_KEY.charCodeAt(index % OBFUSCATION_KEY.length),
    ),
  ).join('');

/** Encodes an acknowledgment to its obfuscated storage form. Exported for test seeding; not secret. */
export const encodeAcknowledgment = (value: StoredAcknowledgment): string =>
  btoa(xorCipher(JSON.stringify(value)));

const decodeAcknowledgment = (raw: unknown): StoredAcknowledgment | undefined => {
  if (typeof raw !== 'string') {
    return undefined;
  }
  try {
    return parseStored(JSON.parse(xorCipher(atob(raw))));
  } catch {
    // Corrupt, tampered, or legacy plaintext — treat as never acknowledged (user re-acknowledges).
    return undefined;
  }
};

/**
 * The full client side of a periodic, versioned acknowledgment (e.g. a legal/PHI disclosure): the
 * localStorage UX gate — persisted per user + kind via the resilient-storage layer (localStorage,
 * with an in-memory fallback when storage is unavailable) — plus the durable audit record POSTed to
 * the BFF, wired together so a caller can't get the order, per-user scoping, or "durable-first"
 * invariant wrong. Re-acknowledgment is required when there is no stored acknowledgment, when its
 * `version` differs from the current one, or when `intervalMonths` has elapsed. The durable record
 * is persisted server-side; the manager that writes it runs only in the BFF.
 */
export const useAcknowledgment = ({
  kind,
  version,
  disclosureText,
  intervalMonths = 6,
}: UseAcknowledgmentOptions): Acknowledgment => {
  const { requestContext, ctwFetch } = useCTW();
  const zusUserId = requestContext.authTokenState.zusUserId;

  // Scope the local gate to both the use case and the user, so acknowledgments never carry over
  // between users sharing a browser profile.
  const [storedValue, setStoredValue] = useResilientStorage<string | null>(
    `${STORAGE_KEY_PREFIX}${kind}:${zusUserId}`,
    null,
    { mode: 'local' },
  );

  const stored = decodeAcknowledgment(storedValue);
  const lastAcknowledged = stored ? parseLocalDate(stored.acknowledgedAt) : undefined;
  const expiresAt = lastAcknowledged ? addMonths(lastAcknowledged, intervalMonths) : undefined;
  const requiresAcknowledgment =
    !stored || stored.version !== version || !expiresAt || Date.now() >= expiresAt.getTime();

  // Read the CURRENT token/fetcher at call time — the gated action may run long enough for the
  // token to refresh, so a render-time capture could go stale. The ref is updated every render.
  const ctwRef = useRef({ authToken: requestContext.authToken, env: requestContext.env, ctwFetch });
  ctwRef.current = { authToken: requestContext.authToken, env: requestContext.env, ctwFetch };

  const recordAcknowledgment = async (
    context: Record<string, string | number | boolean> = {},
  ): Promise<void> => {
    const response = await submitAcknowledgment({
      ctwFetch: ctwRef.current.ctwFetch,
      env: ctwRef.current.env,
      authToken: ctwRef.current.authToken,
      attributes: { kind, version, disclosureText, context },
    });
    if (!response.ok) {
      throw new Error('Failed to record the acknowledgment');
    }
    // Local calendar date (not UTC) so it round-trips with parseLocalDate.
    setStoredValue(
      encodeAcknowledgment({ version, acknowledgedAt: format(new Date(), 'yyyy-MM-dd') }),
    );
  };

  return { requiresAcknowledgment, expiresAt, recordAcknowledgment };
};
